"use client";

import { useState } from "react";
import { Shrink } from "lucide-react";
import { Dropzone } from "@/components/Dropzone";
import { Button, Card, ErrorNote, Field, Select, Stat, Progress } from "@/components/ui";
import { getFfmpeg, kokAd } from "@/lib/ffmpeg";
import { formatBytes } from "@/lib/utils";

const KALITELER: Record<string, { crf: string; ad: string }> = {
  yuksek: { crf: "23", ad: "Yüksek kalite (az küçülür)" },
  orta: { crf: "28", ad: "Orta (dengeli — önerilen)" },
  kucuk: { crf: "32", ad: "Küçük dosya (daha çok küçülür)" },
};

export function SikistirTool() {
  const [dosya, setDosya] = useState<File | null>(null);
  const [ad, setAd] = useState("video");
  const [eski, setEski] = useState(0);
  const [kalite, setKalite] = useState("orta");
  const [ilerleme, setIlerleme] = useState<number | null>(null);
  const [durum, setDurum] = useState("");
  const [cikti, setCikti] = useState<{ url: string; boyut: number } | null>(null);
  const [hata, setHata] = useState("");

  const yukle = (files: File[]) => {
    setDosya(files[0]);
    setAd(kokAd(files[0].name));
    setEski(files[0].size);
    setCikti(null);
    setHata("");
  };

  async function sikistir() {
    if (!dosya) return;
    setHata("");
    setCikti(null);
    setDurum("Video motoru hazırlanıyor…");
    setIlerleme(0);
    try {
      const ff = await getFfmpeg();
      setDurum("Sıkıştırılıyor… (bu işlem biraz sürebilir)");
      ff.on("progress", ({ progress }) => setIlerleme(Math.min(100, Math.round(progress * 100))));
      await ff.writeFile("girdi", new Uint8Array(await dosya.arrayBuffer()));
      await ff.exec([
        "-i", "girdi",
        "-vcodec", "libx264",
        "-crf", KALITELER[kalite].crf,
        "-preset", "veryfast",
        "-acodec", "aac",
        "-b:a", "128k",
        "cikti.mp4",
      ]);
      const data = await ff.readFile("cikti.mp4");
      const blob = new Blob([data as unknown as BlobPart], { type: "video/mp4" });
      setCikti({ url: URL.createObjectURL(blob), boyut: blob.size });
      await ff.deleteFile("girdi").catch(() => {});
      await ff.deleteFile("cikti.mp4").catch(() => {});
    } catch {
      setHata("Sıkıştırma başarısız oldu. Daha küçük/kısa bir video deneyin.");
    } finally {
      setDurum("");
      setIlerleme(null);
    }
  }

  if (!dosya) {
    return (
      <>
        <Dropzone accept="video/*" label="Videonuzu sürükleyin" hint="Kısa videolar hızlı sıkışır · video tarayıcınızdan çıkmaz" onFiles={yukle} />
        <div className="mt-4"><ErrorNote>{hata}</ErrorNote></div>
      </>
    );
  }

  const kazanc = cikti && eski ? Math.max(0, Math.round((1 - cikti.boyut / eski) * 100)) : 0;

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-end gap-4">
        <Field label="Kalite / boyut">
          <Select value={kalite} onChange={(e) => setKalite(e.target.value)} className="w-64">
            {Object.entries(KALITELER).map(([k, v]) => (
              <option key={k} value={k}>{v.ad}</option>
            ))}
          </Select>
        </Field>
        <Button onClick={sikistir} loading={ilerleme !== null}>
          <Shrink className="size-4" /> Sıkıştır
        </Button>
        <button onClick={() => { setDosya(null); setCikti(null); }} className="ml-auto text-sm text-muted underline">Başka video</button>
      </Card>

      {ilerleme !== null && <Progress value={ilerleme / 100} label={durum || `%${ilerleme}`} />}
      <ErrorNote>{hata}</ErrorNote>

      {cikti && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat etiket="Önce" deger={formatBytes(eski)} />
            <Stat etiket="Sonra" deger={formatBytes(cikti.boyut)} />
            <Stat etiket="Kazanç" deger={`%${kazanc}`} vurgu />
          </div>
          <Card className="space-y-3">
            { }
            <video src={cikti.url} controls className="max-h-[50vh] w-full rounded-xl bg-black" />
            <a href={cikti.url} download={`${ad}-sikistirilmis.mp4`} className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-medium text-brand-fg">
              Sıkıştırılan videoyu indir
            </a>
          </Card>
        </>
      )}

      <p className="text-sm text-muted">Yeniden kodlama tarayıcıda (GPU&apos;suz) yavaştır; kısa videolarda idealdir. Video sunucuya gitmez.</p>
    </div>
  );
}
